import React, { useMemo } from 'react';
import { Plus, Trash2, AlertTriangle, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import ConceptoCombobox from '@/components/ConceptoCombobox';
import {
  BASES_LCL,
  ORDEN_GRUPO,
  UNIDAD_DE_BASE,
  calcularConceptosLcl,
  formatImporte,
} from '../utils/lclCotizacionCalc.js';

const MONEDAS = ['USD', 'COP', 'EUR', 'MXN', 'CLP', 'PEN', 'BRL', 'CNY'];

const inputCls =
  'h-8 w-full rounded border border-input bg-background px-2 text-sm ' +
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

/**
 * Tabla editable de conceptos de una cotización LCL.
 *
 * Se agrupa por grupo con subtotal por sección, como el formato de
 * oferta que usa el operador. Los totales que se ven acá son un cálculo
 * local para dar respuesta inmediata; los definitivos los recalcula el
 * servidor al guardar.
 *
 * Props:
 *  - conceptos, onChange: la lista y su setter
 *  - magnitudes: carga de la solicitud (para las bases por W/M, m³, …)
 *  - moneda, monedaSecundaria, trm: para la conversión
 *  - catalogo: conceptos del catálogo del tenant
 *  - readOnly
 */
export default function LclConceptosTable({
  conceptos = [],
  onChange,
  magnitudes = {},
  moneda = 'USD',
  monedaSecundaria = null,
  trm = null,
  catalogo = [],
  readOnly = false,
}) {
  const calc = useMemo(
    () => calcularConceptosLcl(conceptos, magnitudes, { moneda, monedaSecundaria, trm }),
    [conceptos, magnitudes, moneda, monedaSecundaria, trm],
  );

  // Se muestran agrupados pero se editan sobre el array plano: mover una
  // línea de grupo es cambiar un string, no reordenar estructuras.
  const grupos = useMemo(() => {
    const mapa = new Map();
    conceptos.forEach((c, idx) => {
      const g = c.grupo || 'OTROS';
      if (!mapa.has(g)) mapa.set(g, []);
      mapa.get(g).push({ ...c, __idx: idx });
    });
    return [...mapa.entries()].sort(
      (a, b) => (ORDEN_GRUPO[a[0]] ?? 999) - (ORDEN_GRUPO[b[0]] ?? 999),
    );
  }, [conceptos]);

  const actualizar = (idx, campo, valor) => {
    const next = conceptos.map((c, i) => (i === idx ? { ...c, [campo]: valor } : c));
    onChange?.(next);
  };

  /** Al elegir del catálogo se prellenan grupo, base y moneda. */
  const aplicarCatalogo = (idx, item) => {
    if (!item) return;
    const next = conceptos.map((c, i) =>
      i === idx
        ? {
            ...c,
            conceptoCodigo: item.codigo,
            conceptoId: item._id || null,
            concepto: c.concepto?.trim() ? c.concepto : item.nombre,
            grupo: item.grupo,
            baseCobro: c.baseCobro || item.baseCobroDefault || 'Fijo',
            moneda: c.moneda || item.monedaDefault || moneda,
            tarifaMinima:
              c.tarifaMinima ?? (item.tarifaMinimaDefault ?? null),
          }
        : c,
    );
    onChange?.(next);
  };

  const agregar = () => {
    onChange?.([
      ...conceptos,
      {
        concepto: '',
        conceptoCodigo: null,
        grupo: 'OTROS',
        baseCobro: 'Fijo',
        cantidad: null,
        tarifaUnitaria: null,
        tarifaMinima: null,
        moneda,
        origen: 'manual',
      },
    ]);
  };

  const quitar = (idx) => onChange?.(conceptos.filter((_, i) => i !== idx));

  if (conceptos.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Esta cotización todavía no tiene conceptos.
        </p>
        {!readOnly && (
          <Button size="sm" variant="outline" className="mt-3" onClick={agregar}>
            <Plus className="mr-2 h-4 w-4" /> Agregar concepto
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[1000px] text-sm">
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-2 py-2 text-left font-medium">Concepto</th>
              <th className="w-[150px] px-2 py-2 text-left font-medium">Catálogo</th>
              <th className="w-[130px] px-2 py-2 text-left font-medium">Base</th>
              <th className="w-[90px] px-2 py-2 text-right font-medium">Cant.</th>
              <th className="w-[100px] px-2 py-2 text-right font-medium">Tarifa</th>
              <th className="w-[95px] px-2 py-2 text-right font-medium">Mínima</th>
              <th className="w-[80px] px-2 py-2 text-left font-medium">Moneda</th>
              <th className="w-[120px] px-2 py-2 text-right font-medium">Total</th>
              {!readOnly && <th className="w-[40px]" />}
            </tr>
          </thead>
          <tbody>
            {grupos.map(([grupo, filas]) => (
              <React.Fragment key={grupo}>
                <tr className="bg-muted/30">
                  <td
                    colSpan={readOnly ? 7 : 8}
                    className="px-2 py-1.5 text-xs font-semibold tracking-wide"
                  >
                    {grupo}
                  </td>
                  <td className="px-2 py-1.5 text-right text-xs font-semibold">
                    {formatImporte(calc.subtotalPorGrupo[grupo] ?? 0)}
                  </td>
                  {!readOnly && <td />}
                </tr>

                {filas.map((c) => {
                  const idx = c.__idx;
                  const r = calc.lineas[idx] || {};
                  const unidad = UNIDAD_DE_BASE[c.baseCobro];
                  return (
                    <tr key={idx} className="border-t align-top">
                      <td className="px-2 py-1.5">
                        <input
                          className={inputCls}
                          value={c.concepto || ''}
                          placeholder="Nombre del concepto"
                          readOnly={readOnly}
                          onChange={(e) => actualizar(idx, 'concepto', e.target.value)}
                        />
                        {r.faltaMagnitud && (
                          <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-700">
                            <AlertTriangle className="h-3 w-3" />
                            Falta el dato de carga para esta base
                          </div>
                        )}
                      </td>

                      <td className="px-2 py-1.5">
                        <ConceptoCombobox
                          catalogo={catalogo}
                          value={c.conceptoCodigo}
                          disabled={readOnly}
                          onSelect={(item) => aplicarCatalogo(idx, item)}
                        />
                      </td>

                      <td className="px-2 py-1.5">
                        <select
                          className={inputCls}
                          value={c.baseCobro || 'Fijo'}
                          disabled={readOnly}
                          onChange={(e) => actualizar(idx, 'baseCobro', e.target.value)}
                        >
                          {BASES_LCL.map((b) => (
                            <option key={b} value={b}>
                              {b}
                            </option>
                          ))}
                        </select>
                        <select
                          className={cn(inputCls, 'mt-1 text-xs')}
                          value={c.grupo || 'OTROS'}
                          disabled={readOnly}
                          onChange={(e) => actualizar(idx, 'grupo', e.target.value)}
                        >
                          {Object.keys(ORDEN_GRUPO).map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td className="px-2 py-1.5 text-right">
                        <input
                          type="number"
                          step="any"
                          className={cn(inputCls, 'text-right')}
                          value={c.cantidad ?? ''}
                          placeholder={r.cantidad != null ? String(r.cantidad) : 'auto'}
                          readOnly={readOnly}
                          onChange={(e) =>
                            actualizar(
                              idx,
                              'cantidad',
                              e.target.value === '' ? null : Number(e.target.value),
                            )
                          }
                        />
                        {unidad && (
                          <div className="mt-0.5 text-[10px] text-muted-foreground">
                            {unidad}
                          </div>
                        )}
                      </td>

                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          step="any"
                          className={cn(inputCls, 'text-right')}
                          value={c.tarifaUnitaria ?? ''}
                          readOnly={readOnly}
                          onChange={(e) =>
                            actualizar(
                              idx,
                              'tarifaUnitaria',
                              e.target.value === '' ? null : Number(e.target.value),
                            )
                          }
                        />
                      </td>

                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          step="any"
                          className={cn(inputCls, 'text-right')}
                          value={c.tarifaMinima ?? ''}
                          placeholder="—"
                          readOnly={readOnly}
                          onChange={(e) =>
                            actualizar(
                              idx,
                              'tarifaMinima',
                              e.target.value === '' ? null : Number(e.target.value),
                            )
                          }
                        />
                      </td>

                      <td className="px-2 py-1.5">
                        <select
                          className={inputCls}
                          value={c.moneda || moneda}
                          disabled={readOnly}
                          onChange={(e) => actualizar(idx, 'moneda', e.target.value)}
                        >
                          {MONEDAS.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td className="px-2 py-1.5 text-right">
                        <div className="font-medium">
                          {formatImporte(r.total, c.moneda || moneda)}
                        </div>
                        {r.aplicoMinimo && (
                          <Badge
                            variant="outline"
                            className="mt-1 border-amber-300 bg-amber-50 text-[10px] font-normal text-amber-800"
                          >
                            aplicó mínimo
                          </Badge>
                        )}
                        {r.sinTasa && (
                          <div className="mt-1 text-[10px] text-rose-700">
                            sin tasa: no suma
                          </div>
                        )}
                      </td>

                      {!readOnly && (
                        <td className="px-1 py-1.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => quitar(idx)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot className="border-t-2 bg-muted/40">
            <tr>
              <td colSpan={readOnly ? 7 : 8} className="px-2 py-2 text-right font-semibold">
                TOTAL
              </td>
              <td className="px-2 py-2 text-right font-semibold">
                {formatImporte(calc.subtotal, moneda)}
              </td>
              {!readOnly && <td />}
            </tr>
          </tfoot>
        </table>
      </div>

      {calc.totalIncompleto && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Hay conceptos en {calc.monedasSinTasa.join(', ')} y no se cargó la tasa
            de cambio, así que <strong>no entran en el total</strong>. Cargá la TRM
            para poder comparar esta oferta con las demás.
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="flex items-center justify-between">
          <Button size="sm" variant="outline" onClick={agregar}>
            <Plus className="mr-2 h-4 w-4" /> Agregar concepto
          </Button>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Tag className="h-3 w-3" />
            Los conceptos con código del catálogo son los que se pueden comparar
            entre proveedores.
          </p>
        </div>
      )}
    </div>
  );
}
